import React, { useEffect, useState } from "react";
import {
  Flame,
  ShieldCheck,
  AlertTriangle,
  Link2,
  RefreshCw,
  Hash,
  Calendar,
} from "lucide-react";
import LiveTicker from "../components/aegis/LiveTicker";
import { getAegisBurns, verifyAegisBurns, getAegisSupply } from "../lib/api";

function formatProof(n) {
  if (n == null) return "—";
  const x = Number(n);
  if (!Number.isFinite(x)) return "—";
  if (x >= 1_000_000) return `${(x / 1_000_000).toFixed(2)}M`;
  if (x >= 1_000) return `${(x / 1_000).toFixed(1)}k`;
  return Math.round(x).toLocaleString();
}

function formatApt(n) {
  if (n == null) return "—";
  const x = Number(n);
  if (!Number.isFinite(x)) return "—";
  if (x >= 1_000) return `${(x / 1_000).toFixed(2)}k`;
  return x.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function shortHash(h) {
  if (!h) return "—";
  return `${h.slice(0, 8)}…${h.slice(-6)}`;
}

function BurnRow({ burn, index, total }) {
  // APT is what users see and trade. PROOF is the backing receipt, kept
  // small for the auditor's eye but never the headline number.
  const apt = burn.amount_apt ?? (burn.amount_proof || 0) / 1000;
  return (
    <article
      data-testid={`burn-row-${index}`}
      className="border border-rose-500/20 bg-[#0b0b0d] hover:border-rose-500/40 transition-colors"
    >
      <div className="grid sm:grid-cols-[auto_1fr_auto] gap-4 p-4 items-center">
        <div className="shrink-0 flex items-center justify-center w-10 h-10 border border-rose-500/40 bg-rose-500/5">
          <Flame className="h-5 w-5 text-rose-400" />
        </div>
        <div className="min-w-0">
          <div className="flex flex-wrap items-baseline gap-2">
            <span
              className="font-mono text-2xl text-rose-300 leading-none tabular-nums"
              data-testid={`burn-amount-apt-${index}`}
            >
              {formatApt(apt)}
            </span>
            <span className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono">
              AEGIS burned
            </span>
            <span
              className="text-[10px] uppercase tracking-[0.25em] text-zinc-600 font-mono"
              data-testid={`burn-amount-proof-${index}`}
              title={`${(burn.amount_proof || 0).toLocaleString()} PROOF (saved LLM tokens) backed this burn`}
            >
              · ◆ {formatProof(burn.amount_proof)} PROOF
            </span>
            <span className="text-[10px] uppercase tracking-[0.25em] text-zinc-600 font-mono ml-auto sm:ml-2">
              burn #{index + 1} of {total}
            </span>
          </div>
          <div className="text-sm text-zinc-300 mt-1 leading-snug">{burn.reason}</div>
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[10px] uppercase tracking-[0.25em] font-mono text-zinc-500">
            <span className="inline-flex items-center gap-1.5">
              <Calendar className="h-3 w-3" />
              {new Date(burn.burned_at).toLocaleString()}
            </span>
            {burn.subscription_id && (
              <span className="inline-flex items-center gap-1.5">
                <Link2 className="h-3 w-3" /> sub · {burn.subscription_id}
              </span>
            )}
            <span
              className="inline-flex items-center gap-1.5 text-zinc-600"
              title={burn.burn_hash}
            >
              <Hash className="h-3 w-3" /> {shortHash(burn.burn_hash)}
            </span>
          </div>
        </div>
      </div>
    </article>
  );
}

export default function Burns() {
  const [data, setData] = useState(null);
  const [supply, setSupply] = useState(null);
  const [chain, setChain] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([getAegisBurns(200), verifyAegisBurns(), getAegisSupply()])
      .then(([d, v, s]) => {
        if (cancelled) return;
        setData(d);
        setChain(v);
        setSupply(s);
      })
      .catch((e) => !cancelled && setError(e?.message || String(e)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [tick]);

  const burns = (data?.burns || []).slice().reverse(); // newest first for the page
  const chainOk = chain?.chain?.valid;

  return (
    <div className="min-h-[calc(100vh-130px)] bg-[#09090b] text-zinc-100">
      <LiveTicker fallbackMultiplier={supply?.burn_multiplier} />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
        {/* Header */}
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.4em] text-rose-400 mb-1 inline-flex items-center gap-2">
              <Flame className="h-3 w-3" /> △ AEGIS · public burn ledger
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl tracking-[0.15em] text-zinc-100">
              Supply Destruction
            </h1>
            <p className="text-zinc-400 text-sm mt-2 max-w-2xl leading-relaxed">
              Every paid Builder membership burns{" "}
              <span className="text-rose-300 font-mono">40%</span> of its
              value from circulating{" "}
              <span className="text-amber-300 font-mono">AEGIS</span>{" "}
              (with the PROOF backing destroyed in lockstep). Each burn is
              HMAC-signed and hash-chained — anyone can audit by walking
              the chain oldest-first.
            </p>
          </div>
          <button
            onClick={() => setTick((t) => t + 1)}
            data-testid="burns-refresh"
            className="inline-flex items-center gap-2 px-3 py-2 text-[11px] uppercase tracking-[0.2em] border border-zinc-800 text-zinc-400 hover:border-rose-500/60 hover:text-rose-300"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>

        {/* Stat strip */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-6">
          <div className="border border-rose-500/40 bg-rose-500/5 p-4" data-testid="burns-total">
            <div className="text-[10px] uppercase tracking-[0.3em] text-rose-400 mb-1 font-mono">
              Total AEGIS burned
            </div>
            <div className="font-mono text-2xl sm:text-3xl text-rose-300 leading-none tabular-nums">
              {supply ? formatApt(supply.total_burned_apt || 0) : "…"}
            </div>
            <div className="text-[9px] uppercase tracking-[0.25em] text-zinc-500 mt-2 font-mono" title="LLM tokens backing the burned APT (1 APT = 1,000 PROOF)">
              ◆ {supply ? formatProof(supply.total_burned_proof) : "…"} PROOF destroyed
            </div>
          </div>
          <div className="border border-zinc-800 bg-[#0b0b0d] p-4" data-testid="burns-count">
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-1 font-mono">
              Burn events
            </div>
            <div className="font-mono text-2xl sm:text-3xl text-zinc-200 leading-none tabular-nums">
              {data?.count ?? "…"}
            </div>
            <div className="text-[9px] uppercase tracking-[0.25em] text-zinc-500 mt-2 font-mono">
              hash-chained
            </div>
          </div>
          <div
            className={`border p-4 ${
              chain == null
                ? "border-zinc-800 bg-[#0b0b0d]"
                : chainOk
                ? "border-emerald-500/40 bg-emerald-500/5"
                : "border-rose-500/40 bg-rose-500/5"
            }`}
            data-testid="burns-chain-status"
          >
            <div
              className={`text-[10px] uppercase tracking-[0.3em] mb-1 font-mono flex items-center gap-1 ${
                chainOk ? "text-emerald-400" : chain == null ? "text-zinc-500" : "text-rose-400"
              }`}
            >
              {chainOk ? <ShieldCheck className="h-3 w-3" /> : <AlertTriangle className="h-3 w-3" />}
              Chain
            </div>
            <div
              className={`font-mono text-xl leading-none ${
                chainOk ? "text-emerald-300" : chain == null ? "text-zinc-300" : "text-rose-300"
              }`}
            >
              {chain == null ? "verifying…" : chainOk ? "✓ verified" : "✗ broken"}
            </div>
            <div className="text-[9px] uppercase tracking-[0.25em] text-zinc-500 mt-2 font-mono">
              {chain?.chain?.verified ?? 0} burns walked
            </div>
          </div>
        </div>

        {/* List */}
        {loading && !data && (
          <div
            className="text-zinc-500 text-sm py-12 text-center font-mono uppercase tracking-[0.25em]"
            data-testid="burns-loading"
          >
            Loading burn ledger…
          </div>
        )}
        {error && (
          <div
            className="text-rose-400 text-sm py-6 border border-rose-500/40 bg-rose-500/5 px-4 font-mono"
            data-testid="burns-error"
          >
            {String(error)}
          </div>
        )}
        {!loading && !error && burns.length === 0 && (
          <div
            className="text-zinc-500 text-sm py-16 text-center border border-zinc-800 bg-[#0b0b0d]"
            data-testid="burns-empty"
          >
            <div className="font-heading text-lg text-zinc-300 mb-2">
              No burns yet
            </div>
            <p className="text-xs uppercase tracking-[0.25em]">
              The first paid membership will appear here.
            </p>
          </div>
        )}
        {burns.length > 0 && (
          <div className="space-y-2" data-testid="burns-list">
            {burns.map((b, i) => (
              <BurnRow
                key={b.id || b.burn_hash}
                burn={b}
                index={burns.length - 1 - i}
                total={burns.length}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
